/**
 * SignatureField — overlay component for capturing a signature
 * Opens a modal with a canvas wired to signature_pad
 */

import { useRef, useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import SignaturePad from 'signature_pad';
import type { FieldPlacement } from './types';

interface SignatureFieldProps {
  placement: FieldPlacement;
  value: string | null;
  /** Previously adopted signature — click to re-apply without re-signing */
  adoptedSignature?: string | null;
  onCapture: (dataUrl: string) => void;
  fieldIndex: number;
}

type SignatureMode = 'draw' | 'type';

const TYPED_FONT = '32px "Dancing Script", "Brush Script MT", cursive';

export function SignatureField({ value, adoptedSignature, onCapture, fieldIndex }: SignatureFieldProps): JSX.Element {
  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState<SignatureMode>('draw');
  const [typedName, setTypedName] = useState('');
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const padRef = useRef<SignaturePad | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const initPad = useCallback(() => {
    if (!canvasRef.current) return;
    const canvas = canvasRef.current;
    const ratio = window.devicePixelRatio || 1;
    canvas.width = canvas.offsetWidth * ratio;
    canvas.height = canvas.offsetHeight * ratio;
    const ctx = canvas.getContext('2d');
    if (ctx) ctx.scale(ratio, ratio);

    if (padRef.current) {
      padRef.current.off();
    }
    padRef.current = new SignaturePad(canvas, { penColor: '#1a1a1a' });
  }, []);

  useEffect(() => {
    if (isOpen && mode === 'draw') {
      // Wait for canvas to render
      const timer = setTimeout(() => initPad(), 50);
      return () => clearTimeout(timer);
    }
    if (!isOpen && padRef.current) {
      padRef.current.off();
      padRef.current = null;
    }
  }, [isOpen, mode, initPad]);

  function handleClear() {
    if (mode === 'draw' && padRef.current) {
      padRef.current.clear();
    } else {
      setTypedName('');
    }
  }

  function renderTypedToCanvas(): string | null {
    // Create an off-screen canvas (the visible canvas may be hidden in type mode)
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    const ratio = window.devicePixelRatio || 1;
    const w = 400;
    const h = 100;
    canvas.width = w * ratio;
    canvas.height = h * ratio;
    ctx.scale(ratio, ratio);
    ctx.clearRect(0, 0, w, h);
    ctx.font = TYPED_FONT;
    ctx.fillStyle = '#1a1a1a';
    ctx.textBaseline = 'middle';
    ctx.fillText(typedName, 20, h / 2);
    return canvas.toDataURL('image/png');
  }

  function handleApply() {
    if (mode === 'draw') {
      if (!padRef.current || padRef.current.isEmpty()) return;
      onCapture(padRef.current.toDataURL('image/png'));
    } else {
      if (!typedName.trim()) return;
      const dataUrl = renderTypedToCanvas();
      if (dataUrl) onCapture(dataUrl);
    }
    setIsOpen(false);
    setTimeout(() => buttonRef.current?.focus(), 50);
  }

  function handleClose() {
    setIsOpen(false);
    setTimeout(() => buttonRef.current?.focus(), 50);
  }

  function handleModeChange(newMode: SignatureMode) {
    setMode(newMode);
    if (padRef.current) {
      padRef.current.off();
      padRef.current = null;
    }
  }

  return (
    <>
      <button
        ref={buttonRef}
        data-testid={`signature-field-${fieldIndex}`}
        onClick={() => {
          // If there's an adopted signature and this field is empty, apply it directly
          if (!value && adoptedSignature) {
            onCapture(adoptedSignature);
          } else {
            setIsOpen(true);
          }
        }}
        aria-label={value ? 'Click to change signature' : adoptedSignature ? 'Click to apply signature' : 'Click to sign'}
        className="w-full h-full flex items-center justify-center border-2 border-dashed border-primary/60 bg-primary/5 hover:bg-primary/10 rounded cursor-pointer transition-colors min-h-[44px]"
      >
        {value ? (
          <img src={value} alt="Signature" className="max-w-full max-h-full object-contain" />
        ) : adoptedSignature ? (
          <img src={adoptedSignature} alt="Click to apply signature" className="max-w-full max-h-full object-contain opacity-40" />
        ) : (
          <span className="text-xs text-primary font-medium select-none">Click to sign</span>
        )}
      </button>

      {/* Rendered in a portal on document.body: this component lives inside a per-field
          `position:absolute; z-index:10` wrapper (a stacking context), so an in-place modal
          was painted UNDER later fields' overlays (e.g. the date input over the draw canvas). */}
      {isOpen &&
        createPortal(
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
            data-testid="signature-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Signature capture"
            onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
          >
            <div className="bg-card rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
              <h2 className="text-lg font-semibold text-foreground mb-4">Add Signature</h2>

              {/* Mode tabs */}
              <div className="flex border border-border rounded-md mb-4">
                <button
                  data-testid="signature-tab-draw"
                  onClick={() => handleModeChange('draw')}
                  className={`flex-1 py-2 text-sm font-medium rounded-l-md transition-colors min-h-[44px] ${
                    mode === 'draw'
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-card text-muted-foreground hover:bg-accent'
                  }`}
                >
                  Draw
                </button>
                <button
                  data-testid="signature-tab-type"
                  onClick={() => handleModeChange('type')}
                  className={`flex-1 py-2 text-sm font-medium rounded-r-md transition-colors min-h-[44px] ${
                    mode === 'type'
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-card text-muted-foreground hover:bg-accent'
                  }`}
                >
                  Type
                </button>
              </div>

              {/* Canvas area */}
              <div className="paper-surface border border-border rounded-md mb-4" data-testid="signature-paper" style={{ height: '160px' }}>
                {mode === 'draw' ? (
                  <canvas
                    ref={canvasRef}
                    data-testid="signature-canvas"
                    className="w-full h-full rounded-md"
                    style={{ touchAction: 'none' }}
                  />
                ) : (
                  <div className="w-full h-full relative flex items-center">
                    <input
                      type="text"
                      placeholder="Type your full name"
                      value={typedName}
                      onChange={(e) => setTypedName(e.target.value)}
                      className="w-full px-4 text-3xl bg-transparent border-none outline-none"
                      style={{ fontFamily: '"Dancing Script", "Brush Script MT", cursive' }}
                      aria-label="Type your signature name"
                    />
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <button
                  data-testid="signature-clear-btn"
                  onClick={handleClear}
                  className="flex-1 py-2.5 text-sm font-medium text-foreground bg-card border border-border rounded-md hover:bg-accent min-h-[44px]"
                >
                  Clear
                </button>
                <button
                  data-testid="signature-apply-btn"
                  onClick={handleApply}
                  className="flex-1 py-2.5 text-sm font-medium text-primary-foreground bg-primary rounded-md hover:bg-primary/90 disabled:opacity-50 min-h-[44px]"
                >
                  Apply
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
}

export default SignatureField;
